/* eslint-disable @stylistic/semi */
import AJS from '../archive/archive.js'
import onclick from '../util/onclick.js'


import $ from '../util/jquery.js'

/**
 * Handles management of items from GUI/browser.
 */
class Manage {
  /**
   * Fetches a CSRF token to send with a subsequent favorite/unfavorite request.
   * Caches the token so repeated favorite/unfavorite clicks on the same page
   * share one request, matching the backend's token TTL (Auth::CSRF_TOKEN_TTL).
   * @return {Promise<String>} the token, or an empty string if the fetch failed.
   */
  static async get_csrf_token() {
    const CSRF_TOKEN_TTL_MS = 12 * 60 * 60 * 1000;

    const tokenExpired = (Date.now() - Manage._csrfTokenFetchedAt) >= CSRF_TOKEN_TTL_MS;
    if (!Manage._csrfTokenPromise || tokenExpired) {
      Manage._csrfTokenFetchedAt = Date.now();
      Manage._csrfTokenPromise = (async () => {
        try {
          const response = await fetch('/services/csrf-token', { credentials: 'same-origin' });
          const result = await response.json();
          return (result && result.success && result.value && result.value.token) || '';
        } catch {
          return '';
        }
      })();
    }

    const token = await Manage._csrfTokenPromise;
    // Don't cache failures - let the next caller retry.
    if (!token) Manage._csrfTokenPromise = null;

    return token;
  }

  /**
   * Removes the item for the item /details/ page user is visiting from their favorites list.
   * This is when request comes from "Favorite toggle button".
   * Un-stars the UI optimistically, then rolls back if the removal doesn't confirm.
   * @param {HTMLElement} element
   */
  static async remove_favorite_item(element) {
    const $item = $(element)
    const identifier = $item.data('id')
    const itemName = $item.data('fav-collection')

    if (!identifier || !itemName) return;

    // toggle favorite, un_favorite icon classes
    Manage.toggle_favorite_icon($item, false);

    // update favorite count on details page
    Manage.update_favorite_count(false);

    const rollback = () => {
      Manage.toggle_favorite_icon($item, true);
      Manage.update_favorite_count(true);
    };

    const csrfToken = await Manage.get_csrf_token();
    if (!csrfToken) {
      rollback();
      return;
    }

    let success = false;
    try {
      const response = await fetch('/services/offshoot/details-page/favorite.php', {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ identifier }),
      });
      const result = await response.json();
      success = Boolean(result && result.success);
    } catch {
      success = false;
    }

    if (!success) rollback();
  }

  /**
   * Toggle favorite button on details page
   * @param {HTMLElement} element - Toggle button on /details/ page
   * @return {Boolean}
   */
  static toggle_list_status(element) {
    if ($(element).hasClass('favorited'))
      Manage.remove_favorite_item(element, true)  // Un-Favorite list or item
    else
      AJS.modal_go(element, { favorite: 1 })  // Favorite list or item

    return false
  }


  /**
   * Toggle favorite, No_Favorite iconochive classes
   *
   * @param {HTMLElement} $item
   * @param {Boolean} $addFavorite - true if you are favoriting item
   */
  static toggle_favorite_icon($item, addFavorite = true) {
    const $labelEl = $($item).find('span.icon-label')

    if ($item.length) {
      $item.find('span:eq(0)').toggleClass('iconochive-No_Favorite iconochive-favorite');

      if (addFavorite) {
        $item.addClass('favorited');
        $item.attr({
          'data-original-title': 'Unfavorite',
        });
        $labelEl.text('Unfavorite');
      } else {
        $item.removeClass('favorited');
        $item.attr({
          'data-original-title': 'Favorite',
        });
        $labelEl.text('Favorite');
      }
    }
  }

  /**
   * Update favorite count on details page
   *
   * @param {Boolean} increment - if true, increment by 1
   */
  static update_favorite_count(increment = true) {
    const $favoriteStatsElement = $('.favorite-count');
    let favoriteLabel = $favoriteStatsElement.find('.item-stats-summary__label');
    let favoriteCount = $favoriteStatsElement.find('.item-stats-summary__count');

    // create and append if favorites is not exist
    if (!favoriteCount.length) {
      favoriteCount = document.createElement('span');
      favoriteCount.className = 'item-stats-summary__count';
      $favoriteStatsElement.append(favoriteCount)

      favoriteLabel = document.createElement('span');
      favoriteLabel.className = 'item-stats-summary__label';
      $favoriteStatsElement.append(favoriteLabel)
    }

    // grab existing favorite count and convert into number
    let value = Number($(favoriteCount).text().trim().replace(/,/g, ''))
    if (increment)
      value += 1;
    else
      value -= 1;

    $(favoriteLabel).text(value === 1 ? ' Favorite' : ' Favorites');
    $(favoriteCount).text(value.toLocaleString());
  }
} // end class Manage


/**
 * Does setup once DOM and JS ready
 */
$(() => {
  // this is still used on item /details/ pages - the [Favorite] button
  const favoriteElement = $('.js-manage-toggle_list_status');
  if (favoriteElement.length) {
    favoriteElement.bind('click', (event) => {
      event.preventDefault();
      Manage.toggle_list_status(favoriteElement[0])
    });
  }

  // open share model on details page
  onclick('.js-manage-share_button', (e) => AJS.modal_go(e.currentTarget, { ignore_lnk: 1, shown: AJS.embed_codes_adjust }))
})

window.Manage = Manage // promote to global

export { Manage as default }
